import { useEffect, useState, type ReactNode } from "react";

let analyticsInitialized = false;

/**
 * Defers Sentry + PostHog initialization until after the first render.
 * Children render immediately; analytics load in the background.
 * PostHogProvider is mounted only once PostHog is ready so feature flag
 * hooks work as expected.
 */
export function DeferredAnalyticsProvider({ children }: { children: ReactNode }) {
  const [PostHogWrapper, setPostHogWrapper] = useState<{
    Provider: React.ComponentType<{ client: unknown; children: ReactNode }>;
    client: unknown;
  } | null>(null);

  useEffect(() => {
    if (analyticsInitialized) return;
    analyticsInitialized = true;

    const init = async () => {
      // Initialize Sentry
      const { initSentry } = await import("@/lib/sentry");
      initSentry();

      // Initialize PostHog
      const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
      if (posthogKey) {
        const posthogHost = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";
        const [{ default: posthog }, { PostHogProvider }] = await Promise.all([
          import("posthog-js"),
          import("posthog-js/react"),
        ]);

        posthog.init(posthogKey, {
          api_host: posthogHost,
          capture_pageview: true,
          capture_pageleave: true,
          autocapture: false,
          disable_session_recording: true,
        });

        // Make PostHog available globally for non-React code (feature-flags.ts)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).posthog = posthog;

        setPostHogWrapper({ Provider: PostHogProvider as never, client: posthog });
      }
    };

    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => { init(); });
    } else {
      setTimeout(init, 0);
    }
  }, []);

  if (PostHogWrapper) {
    const { Provider, client } = PostHogWrapper;
    return <Provider client={client}>{children}</Provider>;
  }

  return <>{children}</>;
}
